import {
  ICuratorConversation,
  ICuratorConversationStatus,
} from "@src/types/curator";
import { buildMediaUrl } from "@src/utils/mediaUrl";

export function getConversationId(
  conversation: Partial<ICuratorConversation>
): string {
  return conversation.documentId || String(conversation.id);
}

export function getConversationStatus(
  conversation: Partial<ICuratorConversation>
): ICuratorConversationStatus {
  const status =
    conversation.status ??
    (conversation as { attributes?: { status?: ICuratorConversationStatus } })
      .attributes?.status;
  return status || "requested";
}

type ConversationFieldKey = Exclude<keyof ICuratorConversation, "attributes">;

function getConversationAttribute<T>(
  conversation: Partial<ICuratorConversation>,
  key: ConversationFieldKey
): T | undefined {
  const topLevel = conversation[key] as T | undefined;
  if (topLevel !== undefined && topLevel !== null) {
    return topLevel;
  }

  return conversation.attributes?.[key] as T | undefined;
}

export function getLastMessagePreview(
  conversation: Partial<ICuratorConversation>
): string | null {
  const preview = getConversationAttribute<string | null>(
    conversation,
    "lastMessagePreview"
  );
  const trimmed = preview?.trim();
  return trimmed || null;
}

type StrapiNestedClient = {
  data?: {
    attributes?: Record<string, unknown>;
  };
};

function getClientRecord(
  client: Partial<ICuratorConversation>["client"]
): Record<string, unknown> | undefined {
  if (!client) return undefined;

  const nested = client as StrapiNestedClient;
  if (nested.data?.attributes) {
    return nested.data.attributes;
  }

  return client as Record<string, unknown>;
}

export function getClientDisplayName(
  conversation: Partial<ICuratorConversation>
): string {
  const client = getClientRecord(conversation.client);

  const alias = client?.chatAlias as string | null | undefined;
  const name = client?.name as string | null | undefined;
  const username = client?.username as string | null | undefined;

  return (alias || name || username || "Anonymous listener").trim();
}

export function isPeerCurator(
  conversation: Partial<ICuratorConversation>
): boolean {
  const client = getClientRecord(conversation.client);
  if (!client) return false;

  const isCurator = client.isCurator;
  return (
    isCurator === true ||
    isCurator === "true" ||
    isCurator === 1 ||
    isCurator === "1"
  );
}

type StrapiMedia = {
  url?: string;
  formats?: {
    thumbnail?: { url?: string };
    small?: { url?: string };
  };
  data?: { attributes?: StrapiMedia };
};

export function getClientAvatarUrl(
  conversation: Partial<ICuratorConversation>
): string {
  const client = getClientRecord(conversation.client);
  const photo = client?.photo as StrapiMedia | string | null | undefined;
  if (!photo) return "";
  if (typeof photo === "string") return buildMediaUrl(photo);

  const attrs = photo.data?.attributes ?? photo;
  const url =
    attrs.formats?.thumbnail?.url ||
    attrs.formats?.small?.url ||
    attrs.url ||
    "";
  return buildMediaUrl(url);
}

export function getClientDocumentId(
  conversation: Partial<ICuratorConversation>
): string | undefined {
  const client = getClientRecord(conversation.client);
  const documentId = client?.documentId;
  return documentId ? String(documentId) : undefined;
}

function namesMatch(a: string, b: string): boolean {
  return a.localeCompare(b, undefined, { sensitivity: "accent" }) === 0;
}

function resolveSenderUsesCuratorPill(
  messageIsCurator: boolean,
  isSelf: boolean,
  peerIsCurator: boolean | undefined,
  senderId: string,
  peerDocumentId: string | undefined,
  rawDisplayName: string,
  clientName: string | undefined
): boolean {
  if (isSelf) return true;
  if (messageIsCurator) return true;

  if (!peerIsCurator) return false;

  if (peerDocumentId && senderId && senderId === peerDocumentId) {
    return true;
  }

  if (clientName && rawDisplayName && namesMatch(rawDisplayName, clientName)) {
    return true;
  }

  return false;
}

export type MessagesTab =
  | "inbox"
  | "requests"
  | "blocked"
  | "ai_blocked"
  | "ended";

function getPlaylistRecord(
  playlist: Partial<ICuratorConversation>["playlist"]
): Record<string, unknown> | undefined {
  if (!playlist) return undefined;

  const nested = playlist as {
    data?: { attributes?: Record<string, unknown> };
  };
  if (nested.data?.attributes) {
    return nested.data.attributes;
  }

  return playlist as Record<string, unknown>;
}

function getNestedShowTitle(show: unknown): string | undefined {
  if (!show || typeof show !== "object") return undefined;

  const record = show as {
    title?: string;
    data?: { attributes?: { title?: string } };
  };

  return record.data?.attributes?.title || record.title;
}

export function getConversationSubject(
  conversation: Partial<ICuratorConversation>
): string | null {
  const title = getConversationAttribute<string | null>(conversation, "title");
  const trimmed = title?.trim();
  return trimmed || null;
}

export function getConversationPlaylistSource(
  conversation: Partial<ICuratorConversation>
): string | null {
  const playlist = getPlaylistRecord(conversation.playlist);
  if (!playlist) return null;

  const showTitle = getNestedShowTitle(playlist.show);
  const episode =
    playlist.episodeNumber ?? playlist.episode ?? playlist.episodeNo;
  const playlistTitle =
    typeof playlist.title === "string" ? playlist.title.trim() : "";

  if (episode != null && episode !== "") {
    const titlePart = playlistTitle || showTitle;
    if (titlePart) {
      return `EP ${episode} | ${titlePart}`;
    }
    return `EP ${episode}`;
  }

  if (playlistTitle) {
    return playlistTitle;
  }

  if (showTitle) {
    return showTitle;
  }

  return null;
}

export function getConversationTopic(
  conversation: Partial<ICuratorConversation>
): string | null {
  return (
    getConversationPlaylistSource(conversation) ||
    getConversationSubject(conversation)
  );
}

export function getConversationHeaderLines(
  conversation: Partial<ICuratorConversation> | undefined
): { fromLine: string; subjectLine: string | null; topicLine: string | null } {
  const fromName = conversation
    ? getClientDisplayName(conversation)
    : "Unknown";

  const subjectLine = conversation
    ? getConversationSubject(conversation)
    : null;
  const topicLine = conversation ? getConversationTopic(conversation) : null;

  return {
    fromLine: `From: ${fromName}`,
    subjectLine,
    topicLine,
  };
}

export function filterConversationsByTab(
  conversations: Partial<ICuratorConversation>[],
  activeTab: MessagesTab
): Partial<ICuratorConversation>[] {
  return conversations.filter((conversation) => {
    const status = getConversationStatus(conversation);
    switch (activeTab) {
      case "inbox":
        return status === "active";
      case "ended":
        return status === "ended";
      case "requests":
        return status === "requested";
      case "blocked":
        return status === "blocked";
      case "ai_blocked":
        return status === "ai_blocked";
      default:
        return false;
    }
  });
}

export function isConversationUnread(
  conversation: Partial<ICuratorConversation>
): boolean {
  if (conversation.unread) return true;
  return (conversation.unreadCount ?? 0) > 0;
}

export function getConversationPreview(
  conversation: Partial<ICuratorConversation>,
  activeTab: MessagesTab
): string {
  const lastMessagePreview = getLastMessagePreview(conversation);
  if (lastMessagePreview) {
    return lastMessagePreview;
  }

  const status = getConversationStatus(conversation);
  if (status === "ended") return "Conversation ended";
  if (activeTab === "blocked" || activeTab === "ai_blocked") {
    return lastMessagePreview || "Blocked conversation";
  }
  if (activeTab === "requests") {
    return (
      getConversationSubject(conversation) ||
      getConversationPlaylistSource(conversation) ||
      "New message request"
    );
  }
  return getConversationTopic(conversation) || "No messages yet";
}

export function getConversationTimestamp(
  conversation: Partial<ICuratorConversation>
): string | null {
  return (
    getConversationAttribute<string | null>(conversation, "lastMessageAt") ||
    getConversationAttribute<string | null>(conversation, "endedAt") ||
    getConversationAttribute<string | null>(conversation, "acceptedAt") ||
    getConversationAttribute<string | null>(conversation, "updatedAt") ||
    getConversationAttribute<string | null>(conversation, "createdAt") ||
    null
  );
}

function getConversationSortTime(
  conversation: Partial<ICuratorConversation>
): number {
  const timestamp = getConversationTimestamp(conversation);
  if (!timestamp) return 0;

  const time = new Date(timestamp).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function sortConversationsByRecency(
  conversations: Partial<ICuratorConversation>[]
): Partial<ICuratorConversation>[] {
  return [...conversations].sort(
    (a, b) => getConversationSortTime(b) - getConversationSortTime(a)
  );
}

export function getConversationsForTab(
  conversations: Partial<ICuratorConversation>[],
  activeTab: MessagesTab
): Partial<ICuratorConversation>[] {
  return sortConversationsByRecency(
    filterConversationsByTab(conversations, activeTab)
  );
}

export function getCuratorDisplayName(
  conversation: Partial<ICuratorConversation>
): string {
  const rawCurator = conversation.curator as
    | { data?: { attributes?: { name?: string } }; name?: string }
    | undefined;

  if (!rawCurator) return "Curator";

  const nestedName = rawCurator.data?.attributes?.name;
  if (nestedName) return nestedName;

  if (rawCurator.name) return rawCurator.name;

  return "Curator";
}

export function getDisplayMessages(
  historyMessages: Record<string, unknown>[],
  conversation: Partial<ICuratorConversation> | undefined,
  options: NormalizeChatMessageOptions = {}
): NormalizedChatMessage[] {
  const normalizeOptions: NormalizeChatMessageOptions = {
    ...options,
    curatorName:
      options.curatorName ||
      (conversation ? getCuratorDisplayName(conversation) : undefined),
    clientName:
      options.clientName ||
      (conversation ? getClientDisplayName(conversation) : undefined),
    peerIsCurator:
      options.peerIsCurator ??
      (conversation ? isPeerCurator(conversation) : false),
    peerDocumentId:
      options.peerDocumentId ??
      (conversation ? getClientDocumentId(conversation) : undefined),
  };

  const normalized = dedupeNormalizedMessages(
    historyMessages
      .map((message) => normalizeChatMessage(message, normalizeOptions))
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
  );

  if (normalized.length > 0 || !getLastMessagePreview(conversation ?? {})) {
    return normalized;
  }

  const senderIsCuratorRole =
    getConversationAttribute<string | null>(
      conversation ?? {},
      "lastMessageSenderRole"
    ) === "curator";
  const peerIsCurator = isPeerCurator(conversation ?? {});
  const previewSenderName = senderIsCuratorRole
    ? peerIsCurator
      ? normalizeOptions.clientName || "Curator"
      : normalizeOptions.curatorName || "Curator"
    : normalizeOptions.clientName || "Anonymous listener";
  const previewIsSelf =
    senderIsCuratorRole &&
    (!peerIsCurator ||
      Boolean(
        normalizeOptions.curatorName &&
        previewSenderName &&
        namesMatch(previewSenderName, normalizeOptions.curatorName)
      ));

  return [
    {
      id: `preview-${getConversationId(conversation!)}`,
      content: getLastMessagePreview(conversation!)!,
      senderName: previewSenderName,
      isCurator: senderIsCuratorRole,
      isSelf: previewIsSelf,
      createdAt: String(
        getConversationAttribute<string | null>(
          conversation!,
          "lastMessageAt"
        ) ||
          getConversationAttribute<string | null>(conversation!, "updatedAt") ||
          new Date().toISOString()
      ),
    },
  ];
}

export function getEmptyChatHint(
  conversation: Partial<ICuratorConversation> | undefined,
  activeTab: MessagesTab
): string {
  if (!conversation) return "No messages yet";

  const status = getConversationStatus(conversation);
  if (status === "ended") {
    return "This conversation has ended. Messages are read-only.";
  }

  if (activeTab === "requests") {
    const subject = conversation ? getConversationSubject(conversation) : null;
    if (subject) {
      return `Request subject: "${subject}". Accept to reply once messages arrive.`;
    }
    const playlistSource = conversation
      ? getConversationPlaylistSource(conversation)
      : null;
    if (playlistSource) {
      return `Request from "${playlistSource}". Accept to reply once messages arrive.`;
    }
    return "No opening message stored yet. Accept the request to start chatting.";
  }

  if (activeTab === "blocked" || activeTab === "ai_blocked") {
    return "This user is blocked. Unblock to send new messages.";
  }

  const topic = conversation ? getConversationTopic(conversation) : null;
  if (topic) {
    return `No messages yet. Conversation topic: "${topic}".`;
  }

  return "No messages yet. Send a message to start the conversation.";
}

// Whole-day difference between today and the given date (0 = today, 1 =
// yesterday, 2+ = older). Negative values (future-dated messages from clock
// skew) are clamped to 0 by callers treating "<= 0" as today.
function getDayDiffFromToday(date: Date): number {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfMessageDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  return Math.round(
    (startOfToday.getTime() - startOfMessageDay.getTime()) / 86_400_000
  );
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDayMonthYear(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
}

export function formatConversationListTime(value?: string | null): string {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const dayDiff = getDayDiffFromToday(date);

  if (dayDiff <= 0) return formatTime(date);
  if (dayDiff === 1) return "Yesterday";
  return formatDayMonthYear(date);
}

// Message-bubble timestamp: today shows just the time, yesterday prefixes
// "Yesterday", and older messages prefix the full date — always keeping the
// time (e.g. "01:02 PM", "Yesterday 01:02 PM", "20/07/2026 01:02 PM").
export function formatMessageTimestamp(value?: string | null): string {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const time = formatTime(date);
  const dayDiff = getDayDiffFromToday(date);

  if (dayDiff <= 0) return time;
  if (dayDiff === 1) return `Yesterday ${time}`;
  return `${formatDayMonthYear(date)} ${time}`;
}

export function getConversationUnreadCount(
  conversation: Partial<ICuratorConversation>
): number {
  const curatorUnreadCount = getConversationAttribute<number>(
    conversation,
    "curatorUnreadCount"
  );
  if (typeof curatorUnreadCount === "number") {
    return curatorUnreadCount;
  }

  if (typeof conversation.unreadCount === "number") {
    return conversation.unreadCount;
  }
  return conversation.unread ? 1 : 0;
}

export function countConversationsByStatus(
  conversations: Partial<ICuratorConversation>[],
  statuses: ICuratorConversationStatus[]
): number {
  return conversations.filter((conversation) =>
    statuses.includes(getConversationStatus(conversation))
  ).length;
}

// Tab badges that flag "there is something new here" (Requests) count only the
// conversations the curator has not read yet, so opening a request clears the
// badge the same way the mobile app does. Badges that are a plain inventory of
// the tab (Blocked, Ended) keep using countConversationsByStatus.
export function countUnreadConversationsByStatus(
  conversations: Partial<ICuratorConversation>[],
  statuses: ICuratorConversationStatus[]
): number {
  return conversations.filter(
    (conversation) =>
      statuses.includes(getConversationStatus(conversation)) &&
      getConversationUnreadCount(conversation) > 0
  ).length;
}

export function getTotalPendingMessagesCount(
  conversations: Partial<ICuratorConversation>[]
): number {
  return conversations.reduce(
    (sum, conversation) => sum + getConversationUnreadCount(conversation),
    0
  );
}

export function formatMessagesBadgeCount(count: number): string | null {
  if (count <= 0) return null;
  if (count > 99) return "+99";
  return String(count);
}

export function patchConversationListFromRealtimeEvent<
  T extends Partial<ICuratorConversation>,
>(
  conversations: T[],
  event: {
    conversationId?: string;
    lastMessagePreview?: string | null;
    lastMessageAt?: string | null;
    lastMessageSenderRole?: string | null;
    curatorUnreadCount?: number;
  }
): T[] {
  const conversationId = event.conversationId;
  if (!conversationId) return conversations;

  const patched = conversations.map((conversation) => {
    if (getConversationId(conversation) !== conversationId) {
      return conversation;
    }

    return {
      ...conversation,
      ...(event.lastMessagePreview != null && {
        lastMessagePreview: event.lastMessagePreview,
      }),
      ...(event.lastMessageAt != null && {
        lastMessageAt: event.lastMessageAt,
      }),
      ...(event.lastMessageSenderRole != null && {
        lastMessageSenderRole: event.lastMessageSenderRole,
      }),
      ...(typeof event.curatorUnreadCount === "number" && {
        curatorUnreadCount: event.curatorUnreadCount,
      }),
    };
  });

  return sortConversationsByRecency(patched) as T[];
}

export type NormalizedChatMessage = {
  id: string;
  content: string;
  senderName: string;
  isCurator: boolean;
  isSelf: boolean;
  createdAt: string;
};

export type NormalizeChatMessageOptions = {
  curatorId?: number;
  curatorDocumentId?: string;
  curatorName?: string;
  clientName?: string;
  peerIsCurator?: boolean;
  peerDocumentId?: string;
};

function parseTruthyFlag(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

function resolveMessageIsSelf(
  senderId: string,
  messageIsCurator: boolean,
  displayName: string,
  options: NormalizeChatMessageOptions
): boolean {
  const {
    curatorId,
    curatorDocumentId,
    curatorName,
    clientName,
    peerIsCurator,
    peerDocumentId,
  } = options;

  if (senderId === "curator") return true;
  if (curatorDocumentId && senderId === curatorDocumentId) return true;
  if (curatorId && senderId === curatorId.toString()) return true;

  if (peerDocumentId && senderId && senderId === peerDocumentId) {
    return false;
  }

  // Optimistic/local sends may not include a sender id yet.
  if (messageIsCurator && !senderId) return true;

  // Curator ↔ listener: the logged-in curator is the only curator in the room.
  if (messageIsCurator && !peerIsCurator) return true;

  // Curator ↔ curator: fall back to display name when ids differ across IVS/history.
  if (messageIsCurator && peerIsCurator && displayName) {
    if (curatorName && namesMatch(displayName, curatorName)) return true;
    if (clientName && namesMatch(displayName, clientName)) return false;
  }

  return false;
}

export function getMessageRequestId(
  message: Record<string, unknown>
): string | undefined {
  const attributes = (message.attributes as Record<string, unknown>) || {};
  const requestId = message.requestId ?? attributes.requestId;
  return requestId ? String(requestId) : undefined;
}

export function getMessageStableId(message: Record<string, unknown>): string {
  const attributes = (message.attributes as Record<string, unknown>) || {};
  const requestId = getMessageRequestId(message);
  if (requestId) return `req:${requestId}`;

  const messageId = message.messageId ?? message.id;
  if (messageId) return `msg:${String(messageId)}`;

  const createdAt =
    message.timestamp ??
    message.createdAt ??
    (message.sendTime instanceof Date
      ? message.sendTime.toISOString()
      : message.sendTime);

  const content = String(
    message.text ?? message.content ?? attributes.content ?? ""
  );

  return `fallback:${String(createdAt ?? "unknown")}:${content}`;
}

export function mergeChatMessages(
  existing: Record<string, unknown>[],
  incoming: Record<string, unknown>
): Record<string, unknown>[] {
  const incomingKey = getMessageStableId(incoming);
  const index = existing.findIndex(
    (message) => getMessageStableId(message) === incomingKey
  );

  if (index !== -1) {
    const next = [...existing];
    next[index] = { ...existing[index], ...incoming };
    return next;
  }

  return [...existing, incoming];
}

export function normalizeChatMessage(
  message: Record<string, unknown>,
  options: NormalizeChatMessageOptions = {}
): NormalizedChatMessage {
  const {
    curatorId,
    curatorDocumentId,
    curatorName,
    clientName,
    peerIsCurator,
    peerDocumentId,
  } = options;
  const attributes = (message.attributes as Record<string, unknown>) || {};
  const rawIsCurator = message.isCurator ?? attributes.isCurator;
  const rawSenderRole = message.senderRole ?? attributes.senderRole;
  const messageIsCurator =
    parseTruthyFlag(rawIsCurator) || rawSenderRole === "curator";
  const senderId = String(
    message.userDocumentId ??
      attributes.userDocumentId ??
      attributes.senderId ??
      message.userId ??
      ""
  ).trim();
  const sendTime = message.sendTime;
  const createdAt = String(
    message.timestamp ??
      message.createdAt ??
      (sendTime instanceof Date ? sendTime.toISOString() : sendTime) ??
      new Date().toISOString()
  );

  const rawDisplayName = String(
    message.displayName ??
      message.alias ??
      attributes.displayName ??
      attributes.senderName ??
      ""
  ).trim();

  const isSelf = resolveMessageIsSelf(
    senderId,
    messageIsCurator,
    rawDisplayName,
    {
      curatorId,
      curatorDocumentId,
      curatorName,
      clientName,
      peerIsCurator,
      peerDocumentId,
    }
  );

  const isCuratorSender = resolveSenderUsesCuratorPill(
    messageIsCurator,
    isSelf,
    peerIsCurator,
    senderId,
    peerDocumentId,
    rawDisplayName,
    clientName
  );

  let senderName = rawDisplayName;
  if (isSelf) {
    senderName = rawDisplayName || curatorName || "Curator";
  } else if (isCuratorSender) {
    senderName = clientName || rawDisplayName || "Curator";
  } else {
    senderName = clientName || rawDisplayName || "Anonymous listener";
  }

  return {
    id: getMessageStableId(message),
    content: String(
      message.text ?? message.content ?? attributes.content ?? ""
    ),
    senderName,
    isCurator: isCuratorSender,
    isSelf,
    createdAt,
  };
}

function dedupeNormalizedMessages(
  messages: NormalizedChatMessage[]
): NormalizedChatMessage[] {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}
